import React, { useState, useEffect } from "react";
import reportService from "services/reportService";
import invoiceService from "services/invoiceService";
import paymentService from "services/paymentService";
import Swal from "sweetalert2";
import "styles/manager.css";

// Helpers para obtener inicio/fin de la semana actual (lunes a domingo)
const getWeekRange = () => {
  const now = new Date();
  const dayOfWeek = now.getDay(); // 0=dom
  const diffToMonday = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
  const monday = new Date(now);
  monday.setDate(now.getDate() + diffToMonday);
  const sunday = new Date(monday);
  sunday.setDate(monday.getDate() + 6);
  const fmt = (d) => d.toISOString().split("T")[0];
  return { start: fmt(monday), end: fmt(sunday) };
};

export const ReportsView = () => {
  const [stats, setStats] = useState({
    currentMonthTotal: 0,
    monthlyAverage: 0,
    quarterTotal: 0,
    annualProjection: 0,
  });
  const [expenseDistribution, setExpenseDistribution] = useState([]);
  const [costCenterReport, setCostCenterReport] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState({
    fecha_inicio: "",
    fecha_fin: "",
  });

  // ── Pagos de la semana ────────────────────────────────────────────────────
  const week = getWeekRange();
  const [weekRange, setWeekRange] = useState({
    fecha_inicio: week.start,
    fecha_fin: week.end,
  });
  const [downloadingExcel, setDownloadingExcel] = useState(false);

  useEffect(() => {
    loadReportsData();
  }, []);

  const loadReportsData = async () => {
    try {
      setLoading(true);
      const statsResponse = await invoiceService.getInvoiceStats();
      if (!statsResponse.error) {
        const data = statsResponse.data.data;
        setStats({
          currentMonthTotal: data.monto_total || 0,
          monthlyAverage: data.monto_total || 0,
          quarterTotal: data.monto_total || 0,
          annualProjection: data.monto_total || 0,
        });
      }

      const expenseTypeResponse =
        await reportService.generateExpenseTypeReport();
      if (!expenseTypeResponse.error) {
        const expenseData = expenseTypeResponse.data.data || [];
        const total = expenseData.reduce(
          (sum, item) => sum + (item.monto_total || 0),
          0,
        );
        setExpenseDistribution(
          expenseData.map((item) => ({
            category: item.nombre,
            amount: item.monto_total || 0,
            percentage:
              total > 0 ? Math.round((item.monto_total / total) * 100) : 0,
            count: item.cantidad || 0,
          })),
        );
      }

      const costCenterResponse = await reportService.generateCostCenterReport();
      if (!costCenterResponse.error)
        setCostCenterReport(costCenterResponse.data.data || []);
    } catch {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "No se pudieron cargar los reportes",
        timer: 3000,
        showConfirmButton: false,
      });
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (n) =>
    new Intl.NumberFormat("es-MX", {
      style: "currency",
      currency: "MXN",
    }).format(n || 0);

  // ── Descarga Excel pagos de la semana ─────────────────────────────────────
  const handleDownloadWeeklyExcel = async () => {
    if (!weekRange.fecha_inicio || !weekRange.fecha_fin) {
      Swal.fire({
        icon: "warning",
        title: "Fechas requeridas",
        text: "Selecciona el rango de fechas",
        timer: 2500,
      });
      return;
    }
    try {
      setDownloadingExcel(true);
      Swal.fire({
        title: "Generando Excel...",
        text: "Por favor espera",
        allowOutsideClick: false,
        didOpen: () => Swal.showLoading(),
      });
      const res = await paymentService.downloadWeeklyPaymentsExcel(weekRange);
      Swal.close();
      if (!res.error) {
        Swal.fire({
          icon: "success",
          title: "¡Descargado!",
          text: "El reporte Excel de pagos se ha descargado correctamente",
          timer: 2500,
          showConfirmButton: false,
        });
      } else {
        throw new Error("Error en la descarga");
      }
    } catch {
      Swal.close();
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "No se pudo descargar el reporte Excel de pagos",
        timer: 3000,
      });
    } finally {
      setDownloadingExcel(false);
    }
  };

  const handleSetCurrentWeek = () => {
    const w = getWeekRange();
    setWeekRange({ fecha_inicio: w.start, fecha_fin: w.end });
  };

  // ── Otros handlers ────────────────────────────────────────────────────────
  const handleExportExcel = async () => {
    try {
      Swal.fire({
        title: "Generando Excel...",
        text: "Por favor espera",
        allowOutsideClick: false,
        didOpen: () => Swal.showLoading(),
      });
      const params = {};
      if (dateRange.fecha_inicio) params.fecha_inicio = dateRange.fecha_inicio;
      if (dateRange.fecha_fin) params.fecha_fin = dateRange.fecha_fin;
      const response = await reportService.downloadInvoiceReportExcel(params);
      if (!response.error)
        Swal.fire({
          icon: "success",
          title: "¡Descargado!",
          text: "El reporte Excel se ha descargado correctamente",
          timer: 2000,
          showConfirmButton: false,
        });
    } catch {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "No se pudo exportar el reporte a Excel",
        timer: 3000,
      });
    }
  };

  const handleExportPDF = async () => {
    try {
      Swal.fire({
        title: "Generando PDF...",
        text: "Por favor espera",
        allowOutsideClick: false,
        didOpen: () => Swal.showLoading(),
      });
      const params = {};
      if (dateRange.fecha_inicio) params.fecha_inicio = dateRange.fecha_inicio;
      if (dateRange.fecha_fin) params.fecha_fin = dateRange.fecha_fin;
      const response = await reportService.downloadInvoiceReportPDF(params);
      if (!response.error)
        Swal.fire({
          icon: "success",
          title: "¡Descargado!",
          text: "El reporte PDF se ha descargado correctamente",
          timer: 2000,
          showConfirmButton: false,
        });
    } catch {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "No se pudo exportar el reporte a PDF",
        timer: 3000,
      });
    }
  };

  const handleSendEmail = async () => {
    const result = await Swal.fire({
      title: "Enviar Reporte por Email",
      html: `
        <input id="email-to" type="email" class="swal2-input" placeholder="destinatario@ejemplo.com">
        <select id="email-format" class="swal2-input">
          <option value="excel">Excel</option>
          <option value="pdf">PDF</option>
        </select>`,
      showCancelButton: true,
      confirmButtonText: "Enviar",
      cancelButtonText: "Cancelar",
      preConfirm: () => {
        const email = document.getElementById("email-to").value;
        const format = document.getElementById("email-format").value;
        if (!email) {
          Swal.showValidationMessage("Ingresa un email válido");
          return false;
        }
        return { email, format };
      },
    });
    if (result.isConfirmed) {
      Swal.fire({
        icon: "info",
        title: "Funcionalidad en desarrollo",
        text: `Se enviará reporte ${result.value.format.toUpperCase()} a ${result.value.email}`,
        timer: 3000,
      });
    }
  };

  const handleGenerateReport = async () => {
    if (!dateRange.fecha_inicio || !dateRange.fecha_fin) {
      Swal.fire({
        icon: "warning",
        title: "Atención",
        text: "Selecciona un rango de fechas",
        timer: 2000,
        showConfirmButton: false,
      });
      return;
    }

    try {
      Swal.fire({
        title: "Generando reporte...",
        allowOutsideClick: false,
        didOpen: () => Swal.showLoading(),
      });

      const response = await reportService.generateInvoiceReport(dateRange);

      if (!response.error && response.data?.success) {
        const facturas = response.data.data?.facturas || [];
        const totales = response.data.data?.totales || {
          cantidad: 0,
          subtotal: 0,
          iva: 0,
          total: 0,
        };

        Swal.fire({
          icon: "success",
          title: "Reporte Generado",
          html: `
          <p><strong>Facturas encontradas:</strong> ${totales.cantidad}</p>
          <p><strong>Subtotal:</strong> ${formatCurrency(totales.subtotal)}</p>
          <p><strong>IVA:</strong> ${formatCurrency(totales.iva)}</p>
          <p><strong>Total:</strong> ${formatCurrency(totales.total)}</p>
        `,
          confirmButtonText: "Cerrar",
        });
      } else {
        Swal.fire({
          icon: "warning",
          title: "Sin resultados",
          text: "No se encontraron facturas en ese rango",
        });
      }
    } catch (error) {
      console.error(error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "No se pudo generar el reporte",
        timer: 3000,
      });
    }
  };

  if (loading) {
    return (
      <div className="reports-view">
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Cargando reportes...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="reports-view">
      {/* ── KPI cards ── */}
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-label">Total Mes Actual</div>
          <div className="stat-value">
            {formatCurrency(stats.currentMonthTotal)}
          </div>
          <div className="stat-change positive">Total acumulado</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Promedio Mensual</div>
          <div className="stat-value">
            {formatCurrency(stats.monthlyAverage)}
          </div>
          <div className="stat-change">Estimado</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Total Trimestre</div>
          <div className="stat-value">{formatCurrency(stats.quarterTotal)}</div>
          <div className="stat-change positive">Acumulado</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Proyección Anual</div>
          <div className="stat-value">
            {formatCurrency(stats.annualProjection)}
          </div>
          <div className="stat-change positive">Estimado 2026</div>
        </div>
      </div>

      {/* ── PAGOS DE LA SEMANA — descarga Excel ─────────────────────────────── */}
      <div
        className="card border-0 shadow-sm"
        style={{ borderTop: "4px solid #28a745" }}
      >
        {/* HEADER */}
        <div className="card-header d-flex align-items-center gap-2 ">
          <i className="bi bi-file-earmark-excel fs-4"></i>
          <span className="fw-semibold">
            Pagos de la Semana — Reporte Excel
          </span>
        </div>

        <div className="card-body">
          {/* INFO BOX */}
          <div className="alert alert-success d-flex align-items-start gap-2">
            <i className="bi bi-bar-chart-line fs-5"></i>
            <div>
              Descarga un archivo Excel con todos los pagos a realizar en el
              período seleccionado. Incluye dos hojas: <strong>Pagos</strong> y{" "}
              <strong>Complementos PPD</strong>.
            </div>
          </div>

          {/* CONTROLES EN UNA SOLA LINEA */}
          <div className="row g-3 align-items-end">
            {/* Fecha Inicio */}
            <div className="col-12 col-md-3">
              <label className="form-label fw-semibold">Fecha Inicio</label>
              <input
                type="date"
                className="form-control"
                value={weekRange.fecha_inicio}
                onChange={(e) =>
                  setWeekRange((prev) => ({
                    ...prev,
                    fecha_inicio: e.target.value,
                  }))
                }
              />
            </div>

            {/* Fecha Fin */}
            <div className="col-12 col-md-3">
              <label className="form-label fw-semibold">Fecha Fin</label>
              <input
                type="date"
                className="form-control"
                value={weekRange.fecha_fin}
                onChange={(e) =>
                  setWeekRange((prev) => ({
                    ...prev,
                    fecha_fin: e.target.value,
                  }))
                }
              />
            </div>

            {/* Botón Semana Actual */}
            <div className="col-12 col-md-auto">
              <button
                onClick={handleSetCurrentWeek}
                className="btn btn-outline-secondary w-100"
              >
                <i className="bi bi-calendar-event me-2"></i>
                Semana actual
              </button>
            </div>

            {/* Botón Descargar */}
            <div className="col-12 col-md-auto">
              <button
                onClick={handleDownloadWeeklyExcel}
                disabled={
                  downloadingExcel ||
                  !weekRange.fecha_inicio ||
                  !weekRange.fecha_fin
                }
                className="btn btn-success w-100 px-4"
              >
                {downloadingExcel ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2"></span>
                    Generando...
                  </>
                ) : (
                  <>
                    <i className="bi bi-file-earmark-excel me-2"></i>
                    Descargar Excel
                  </>
                )}
              </button>
            </div>
          </div>

          {/* PERIODO SELECCIONADO (ABAJO) */}
          {weekRange.fecha_inicio && weekRange.fecha_fin && (
            <div className="mt-3 text-muted small">
              <i className="bi bi-calendar-range me-1"></i>
              Período seleccionado:
              <strong className="ms-1">{weekRange.fecha_inicio}</strong> al{" "}
              <strong>{weekRange.fecha_fin}</strong>
            </div>
          )}
        </div>
      </div>

      {/* ── Distribución por tipo de gasto ── */}
      {expenseDistribution.length > 0 && (
        <div className="chart-container">
          <div className="chart-title">Distribución por Tipo de Gasto</div>
          <div className="expense-distribution">
            {expenseDistribution.map((expense, index) => (
              <div key={index} className="expense-item">
                <div className="expense-header">
                  <span className="expense-category">
                    {expense.category} ({expense.count} facturas)
                  </span>
                  <span className="expense-amount">
                    {formatCurrency(expense.amount)} ({expense.percentage}%)
                  </span>
                </div>
                <div className="progress-bar-container">
                  <div
                    className="progress-bar"
                    style={{ width: `${expense.percentage}%` }}
                  ></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── Reporte personalizado ── */}
      {/* <div className="card shadow-sm">
        <div className="card-header d-flex align-items-center gap-2">
          <i className="bi bi-funnel-fill "></i>
          <span className="fw-semibold">Generar Reporte Personalizado</span>
        </div>

        <div className="card-body">
          <div className="row g-3 align-items-end">
            <div className="col-12 col-md-4">
              <label className="form-label fw-semibold">Fecha Inicio</label>
              <input
                type="date"
                className="form-control"
                value={dateRange.fecha_inicio}
                onChange={(e) =>
                  setDateRange({ ...dateRange, fecha_inicio: e.target.value })
                }
              />
            </div>
            <div className="col-12 col-md-4">
              <label className="form-label fw-semibold">Fecha Fin</label>
              <input
                type="date"
                className="form-control"
                value={dateRange.fecha_fin}
                onChange={(e) =>
                  setDateRange({ ...dateRange, fecha_fin: e.target.value })
                }
              />
            </div>
            ¿
            <div className="col-12 col-md-auto">
              <button
                className="btn btn-primary w-100 px-4"
                onClick={handleGenerateReport}
                disabled={!dateRange.fecha_inicio || !dateRange.fecha_fin}
              >
                <i className="bi bi-bar-chart-line me-2"></i>
                Generar Reporte
              </button>
            </div>
          </div>
        </div>
      </div> */}

      {/* ── Exportar reportes ── */}
      <div className="card">
        <div className="card-header">Exportar Reportes</div>
        <div className="export-buttons">
          <button className="btn btn-export" onClick={handleExportExcel}>
            <i className="bi bi-file-earmark-excel"></i> Exportar a Excel
          </button>
          <button className="btn btn-export" onClick={handleExportPDF}>
            <i className="bi bi-file-earmark-pdf"></i> Exportar a PDF
          </button>
          <button className="btn btn-export" onClick={handleSendEmail}>
            <i className="bi bi-envelope"></i> Enviar por Email
          </button>
        </div>
      </div>

      {/* ── Resumen por centro de costo ── */}
      {costCenterReport.length > 0 && (
        <div className="card">
          <div className="card-header">Resumen por Centro de Costo</div>
          <div className="financial-summary">
            {costCenterReport.map((cc, index) => (
              <div key={index} className="summary-row">
                <span className="summary-label">
                  {cc.nombre} - {cc.descripcion}
                </span>
                <span className="summary-value">
                  {formatCurrency(cc.monto_total)} ({cc.total_facturas}{" "}
                  facturas)
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
